import { toast } from "react-toastify";
import { postApi, putApi } from "../../Services/apiService";
import {
  getFamilyTreeIdFromStorage,
  setFamilyTreeIdInLoggedInRole,
} from "../headOfLineRole";
import { areAllDateValidationsPassing } from "../dateValidationUtils";
import { convertDateToTimestamp } from "../dateUtils";
import { extractPhoneNumber } from "../phone";
import { resolveEmailValidationError } from "./email";
import { isInRelationshipMaritalStatus } from "./dropdown";
import {
  getPrimaryFallbackDob,
  isEmailRequiredForMember,
} from "./memberAge";


export const handleFormSubmit = async (params) => {
    const {
        e,
        // State setters
        setFormSubmitted,
        setDateValidationErrors,
        setImageValidationError,
        setSpouseImageValidationError,
        setSpouseValidationErrors,
        setAdditionalSpouseImageErrors,
        setAdditionalSpouseErrors,
        setTouchedFields,
        setFieldValidationErrors,
        setLoading,
        // State values
        hasEmailConflict,
        grandfatherData,
        grandmotherData,
        spouses,
        uploadedImages,
        dateValidationErrors,
        isEditMode,
        editMemberData,
        parentMember,
        parentGeneration,
        currentGeneration,
        parentChildren = [],
        familyTreeId,
        rootHeadData,
        selectedProfileColor,
        // Section accordion setters (used to reveal collapsed sections on error)
        setOpenSection,
        setOpenSpouseSections,
        // Functions
        validateDates,
        // Callbacks
        onNext,
        onClose,
      } = params;
    e.preventDefault();

    // Mark form as submitted to show all validation errors
    setFormSubmitted(true);

    // Validation errors are only visible when their section is expanded, so any
    // failed validation must first re-open the section that owns the field.
    const revealPrimarySection = () => {
      if (setOpenSection) setOpenSection("primary");
    };
    const revealSpouseSection = (spouseId) => {
      if (setOpenSpouseSections) {
        setOpenSpouseSections((prev) =>
          prev.includes(spouseId) ? prev : [...prev, spouseId]
        );
      }
    };

    // Block submission if any email conflict is present
    if (hasEmailConflict) {
      revealPrimarySection();
      toast.error("Please fix the email error before saving");
      return;
    }

    // Reset validation errors
    setDateValidationErrors({
      primary: { dob: "", dod: "", weddingAnniversary: "" },
      spouse: { dob: "", dod: "", weddingAnniversary: "" },
    });

    // debugger
    // Check if this is a root user (related_member_id is null)
    const isRootUser = isEditMode

      ? editMemberData?.related_member_id === null || editMemberData?.related_member_id === undefined
      : !parentMember || (parentMember && !parentMember.id);

    // Profile image validation
    // First main image is not required for root users
    // Additional primary user images are required even for root users (if uploaded)
    const mainImages = uploadedImages.filter((img) => img.type === "main");
    const hasMainImage = mainImages.length > 0;

    if (parentGeneration != 0 || editMemberData?.generation != 0) {
      // For non-root users, at least one main image is required
      if (!isRootUser && !hasMainImage) {
        setImageValidationError(true);
        toast.error("Profile image is required");
        return;
      }

      // For root users: first main image is optional, but if multiple main images exist,
      // all must be valid (additional primary user images are required to be complete)
      if (isRootUser && mainImages.length > 1) {
        const invalidMainImages = mainImages.filter(img => !img.file && !img.url && !img.unique_image_id);
        if (invalidMainImages.length > 0) {
          setImageValidationError(true);
          toast.error("All primary user images must be valid");
          return;
        }
      }
    }

    // Validate marital status first
    let hasErrors = false;
    const errors = {
      maritalStatus: "",
      gender: "",
      stateOfBirth: "",
      aliveStatus: "",
      email: "",
      firstName: "",
      lastName: "",
      specialName: "",
      multipleBirthSiblings: "",
      multipleBirthOrders: "",
    };

    // Validate firstName (trimmed value)
    if (!grandfatherData.firstName || grandfatherData.firstName.trim() === "") {
      errors.firstName = "First Name is required";
      hasErrors = true;
    }

    // Validate lastName (trimmed value)
    if (!grandfatherData.lastName || grandfatherData.lastName.trim() === "") {
      errors.lastName = "Last Name is required";
      hasErrors = true;
    }
    
    // Helper function to extract first name from child object
    const getChildFirstName = (child) => {
      // First try first_name field
      if (child.first_name) {
        return child.first_name.trim();
      }
      // If not available, try name field and extract first word (before space)
      if (child.name) {
        return child.name.trim().split(' ')[0];
      }
      return "";
    };
    
    // Helper function to get suffix from child object
    const getChildSuffix = (child) => {
      // First try suffix field directly
      if (child.suffix) {
        return child.suffix.trim().toLowerCase();
      }
      // If suffix field is not available, try to extract from name field
      // Suffix is typically the last part after the last space (e.g., "John Smith Jr" -> "Jr")
      if (child.name) {
        const nameParts = child.name.trim().split(' ');
        // Common suffixes: Jr, Sr, II, III, IV, etc.
        const commonSuffixes = ['jr', 'sr', 'ii', 'iii', 'iv', 'v', 'vi', 'vii', 'viii', 'ix', 'x'];
        if (nameParts.length > 1) {
          const lastPart = nameParts[nameParts.length - 1].toLowerCase();
          // Check if last part is a common suffix
          if (commonSuffixes.includes(lastPart) || /^(i{1,3}|iv|v|vi{0,3}|ix|x)$/i.test(lastPart)) {
            return lastPart;
          }
        }
      }
      return "";
    };
    
    // Validate special initial for generation 2 if firstName matches any child AND suffixes match (or both empty)
    const isGeneration2 = currentGeneration === 2;
    if (isGeneration2 && grandfatherData.firstName) {
      const enteredFirstName = grandfatherData.firstName.trim().toLowerCase();
      const enteredSuffix = (grandfatherData.suffix || "").trim().toLowerCase();
      const hasMatch = parentChildren.some(child => {
        const childFirstName = getChildFirstName(child).toLowerCase();
        const childSuffix = getChildSuffix(child);
        // In edit mode, exclude the current member being edited
        if (isEditMode && editMemberData?.id && child.id === editMemberData.id) {
          return false;
        }
        // Check if first names match
        if (childFirstName !== enteredFirstName || childFirstName === "") {
          return false;
        }
        // Check suffixes: require special initial if both no suffix or both same suffix
        // Different suffixes → don't require special initial
        const bothNoSuffix = enteredSuffix === "" && childSuffix === "";
        const bothSameSuffix = enteredSuffix !== "" && childSuffix !== "" && enteredSuffix === childSuffix;
        return bothNoSuffix || bothSameSuffix;
      });
      
      if (hasMatch && (!grandfatherData.specialName || grandfatherData.specialName.trim() === "")) {
        errors.specialName = "Special initial is required when first name matches an existing child.";
        hasErrors = true;
      }
    }

    if (!grandfatherData.maritalStatus || grandfatherData.maritalStatus.trim() === "") {
      errors.maritalStatus = "Marital Status is required";
      hasErrors = true;
    }

    if (!grandfatherData.gender) {
      errors.gender = "Gender is required";
      hasErrors = true;
    }

    //Validate state of birth
    if (!grandfatherData.stateOfBirth || grandfatherData.stateOfBirth.trim() === "") {
      errors.stateOfBirth = "State of Birth is required";
      hasErrors = true;
    }

    // Validate living status
    if (!grandfatherData.aliveStatus || grandfatherData.aliveStatus.trim() === "") {
      errors.aliveStatus = "Living / Deceased status is required";
      hasErrors = true;
    }

    // Validate email - required for living members; optional under 18 in edit mode, under 13 when adding
    if (grandfatherData.aliveStatus === "1") {
      const emailError = resolveEmailValidationError(grandfatherData.email, {
        required: isEmailRequiredForMember({
          isEditMode,
          isDeceased: false,
          dob: grandfatherData.dob,
          currentAge: grandfatherData.currentAge,
          fallbackDob: getPrimaryFallbackDob(editMemberData),
        }),
      });
      if (emailError) {
        errors.email = emailError;
        hasErrors = true;
      }
    } else if (grandfatherData.email?.trim()) {
      const emailError = resolveEmailValidationError(grandfatherData.email);
      if (emailError) {
        errors.email = emailError;
        hasErrors = true;
      }
    }

    // Validate multiple birth siblings selection
    if (grandfatherData.multiple_births_type) {
      const multipleBirthsType = typeof grandfatherData.multiple_births_type === 'string' 
        ? parseInt(grandfatherData.multiple_births_type, 10) 
        : grandfatherData.multiple_births_type;
      
      if (multipleBirthsType >= 2 && multipleBirthsType <= 9) {
        const requiredSiblingCount = multipleBirthsType - 1; // For twin (2), need 1 sibling; triplet (3), need 2 siblings, etc.
        const multipleBirthDetails = grandfatherData.multiple_birth_details || [];
        
        // In edit mode, exclude current member from count (they're already part of the set)
        // In add mode, count all entries (current member has family_member_id: null)
        let selectedCount;
        if (isEditMode && editMemberData?.id) {
          const currentMemberId = editMemberData.id.toString();
          selectedCount = multipleBirthDetails.filter(detail => {
            const detailId = detail.family_member_id?.toString() || "";
            // Exclude current member from count
            return !(detailId && currentMemberId && detailId === currentMemberId);
          }).length;
        } else {
          selectedCount = Array.isArray(multipleBirthDetails) ? multipleBirthDetails.length : 0;
        }
        
        if (selectedCount !== requiredSiblingCount) {
          errors.multipleBirthSiblings = `Please select exactly ${requiredSiblingCount} sibling${requiredSiblingCount > 1 ? 's' : ''} with the same DOB (or within ±3 days)`;
          hasErrors = true;
        } else {
          // Check if all selected siblings have orders assigned (excluding current member in edit mode)
          const currentMemberId = isEditMode && editMemberData?.id ? editMemberData.id.toString() : null;
          const siblingsWithoutOrders = multipleBirthDetails.filter(d => {
            // In edit mode, exclude current member from this check (they have their own order field)
            if (currentMemberId) {
              const detailId = d.family_member_id?.toString() || "";
              if (detailId === currentMemberId) {
                return false; // Skip current member
              }
            }
            // Check if order is missing
            return !d.order || d.order === "" || d.order === null || d.order === undefined;
          });
          
          if (siblingsWithoutOrders.length > 0) {
            errors.multipleBirthOrders = "Please assign birth orders to all selected siblings";
            hasErrors = true;
          } else {
            // Validate birth orders
            const currentMemberId = isEditMode && editMemberData?.id ? editMemberData.id.toString() : null;
            
            // Get current member's order - check both multiple_birth_order field and their entry in multiple_birth_details
            let currentMemberOrder = null;
            if (currentMemberId) {
              // First, try to get order from their entry in multiple_birth_details
              const currentMemberEntry = multipleBirthDetails.find(d => {
                const detailId = d.family_member_id?.toString() || "";
                return detailId === currentMemberId;
              });
              
              if (currentMemberEntry?.order) {
                const order = currentMemberEntry.order;
                currentMemberOrder = typeof order === 'string' ? parseInt(order, 10) : order;
                if (isNaN(currentMemberOrder)) currentMemberOrder = null;
              }
            }
            
            // If not found in multiple_birth_details, use the separate field
            if (!currentMemberOrder) {
              currentMemberOrder = grandfatherData.multiple_birth_order 
                ? (typeof grandfatherData.multiple_birth_order === 'string' 
                    ? parseInt(grandfatherData.multiple_birth_order, 10) 
                    : grandfatherData.multiple_birth_order)
                : null;
              if (currentMemberOrder && isNaN(currentMemberOrder)) currentMemberOrder = null;
            }
            
            // Get sibling orders (excluding current member in edit mode)
            const siblingOrders = multipleBirthDetails
              .filter(d => {
                // In edit mode, exclude current member's entry from sibling orders
                if (currentMemberId) {
                  const detailId = d.family_member_id?.toString() || "";
                  if (detailId === currentMemberId) {
                    return false; // Skip current member
                  }
                }
                return true;
              })
              .map(d => d.order)
              .map(order => typeof order === 'string' ? parseInt(order, 10) : order)
              .filter(order => !isNaN(order));
            
            // Collect all orders including current member
            const allOrders = [...siblingOrders];
            if (currentMemberOrder && !isNaN(currentMemberOrder)) {
              allOrders.push(currentMemberOrder);
            }
            
            // Check for duplicates
            const uniqueOrders = new Set(allOrders);
            if (allOrders.length !== uniqueOrders.size) {
              errors.multipleBirthOrders = "Each member must have a unique birth order";
              hasErrors = true;
            }
            
            // Check if all orders are within valid range
            const invalidOrders = allOrders.filter(order => order < 1 || order > multipleBirthsType);
            if (invalidOrders.length > 0) {
              errors.multipleBirthOrders = `Birth orders must be between 1 and ${multipleBirthsType}`;
              hasErrors = true;
            }
            
            // Check if all orders from 1 to multipleBirthsType are assigned
            if (allOrders.length === multipleBirthsType) {
              const sortedOrders = [...allOrders].sort((a, b) => a - b);
              const expectedOrders = Array.from({ length: multipleBirthsType }, (_, i) => i + 1);
              const hasAllOrders = expectedOrders.every(order => sortedOrders.includes(order));
              
              if (!hasAllOrders) {
                errors.multipleBirthOrders = `All birth orders from 1 to ${multipleBirthsType} must be assigned`;
                hasErrors = true;
              }
            } else if (allOrders.length > 0) {
              // Some orders are missing
              errors.multipleBirthOrders = `Please assign birth orders for all ${multipleBirthsType} members`;
              hasErrors = true;
            }
          }
        }
      }
    }





    // Set validation errors
    setFieldValidationErrors(errors);

    // Mark all fields as touched to show errors
    setTouchedFields((prev) => ({
      ...prev,
      primary: {
        ...prev.primary,
        firstName: true,
        lastName: true,
        maritalStatus: true,
        email: true,
        aliveStatus: true,
        specialName: true,
        multipleBirthSiblings: true,
      },
    }));

    if (hasErrors) {
      // The primary section may be collapsed - open it so the errors are visible
      revealPrimarySection();
      toast.error(
        Object.values(errors).find((message) => message) ||
        "Please fix the highlighted fields"
      );
      // Scroll to first error field and try to focus
      setTimeout(() => {
        if (errors.firstName) {
          const firstNameField = document.querySelector('#grandfather-firstName');
          if (firstNameField) {
            firstNameField.scrollIntoView({ behavior: 'smooth', block: 'center' });
            firstNameField.focus();
          }
        } else if (errors.lastName) {
          const lastNameField = document.querySelector('#grandfather-lastName');
          if (lastNameField) {
            lastNameField.scrollIntoView({ behavior: 'smooth', block: 'center' });
            lastNameField.focus();
          }
        } else if (errors.maritalStatus) {
          const maritalStatusField = document.querySelector('#marital-status-field');
          if (maritalStatusField) {
            maritalStatusField.scrollIntoView({ behavior: 'smooth', block: 'center' });
            const input = maritalStatusField.querySelector('input, select, div[role="combobox"]');
            input?.focus();
          }
        } else if (errors.gender) { // Add this condition
          const genderField = document.querySelector('#gender-field');
          if (genderField) {
            genderField.scrollIntoView({ behavior: 'smooth', block: 'center' });
            const input = genderField.querySelector('input, select, div[role="combobox"]');
            input?.focus();
          }
        } else if (errors.specialName) {
          const specialNameField = document.querySelector('#grandfather-specialName');
          if (specialNameField) {
            specialNameField.scrollIntoView({ behavior: 'smooth', block: 'center' });
            specialNameField.focus();
          }
        } else if (errors.stateOfBirth) {
          const stateOfBirthField = document.querySelector('#state-of-birth');
          if (stateOfBirthField) {
            stateOfBirthField.scrollIntoView({ behavior: 'smooth', block: 'center' });
            const input = stateOfBirthField.querySelector('input, select, div[role="combobox"]');
            input?.focus();
          }
        } else if (errors.aliveStatus) {
          const aliveStatusField = document.querySelector('#alive-status-field');
          if (aliveStatusField) {
            aliveStatusField.scrollIntoView({ behavior: 'smooth', block: 'center' });
            const input = aliveStatusField.querySelector('input, select, div[role="combobox"]');
            input?.focus();
          }
        }
        else if (errors.email) {
          const emailField = document.querySelector('[name="email"]');
          if (emailField) {
            emailField.scrollIntoView({ behavior: 'smooth', block: 'center' });
            emailField.focus();
          }
        } else if (errors.multipleBirthSiblings) {
          // Scroll to multiple birth siblings field
          const multipleBirthField = document.querySelector('[class*="multiple-birth"]') || 
                                     document.querySelector('[placeholder*="Select siblings"]')?.closest('.form-group');
          if (multipleBirthField) {
            multipleBirthField.scrollIntoView({ behavior: 'smooth', block: 'center' });
          }
        }



      }, 100);
      return;
    }

    const issingle = grandfatherData.maritalStatus == 1;
    const skipDivorceAndWeddingRequired = [3, 4].includes(
      Number(grandfatherData.maritalStatus)
    );

    // 🔴 Spouse image required if spouse section is shown (i.e., not Single)
    // Main spouse image is not required for root users
    // Only check main spouse image if marital status is selected and it's not Single
    if (!issingle && !isRootUser && (parentGeneration != 0 || editMemberData?.generation != 0) && grandfatherData?.maritalStatus) {
      // Validate main spouse image
      const hasMainSpouseImage = uploadedImages.some(
        (img) => img.type === "spouse" || img.type === "spouse-main-spouse"
      );
      if (!hasMainSpouseImage) {
        setSpouseImageValidationError(true);
        setSpouseValidationErrors((prev) => ({
          ...prev,
          image: "Spouse image is required",
        }));
        revealSpouseSection("main-spouse");
        toast.error("Spouse image is required");
        setTimeout(() => {
          const spouseImageSection = document.querySelector('#spouse-main-image-section');
          if (spouseImageSection) {
            spouseImageSection.scrollIntoView({ behavior: 'smooth', block: 'center' });
          }
        }, 100);
        return;
      }

      // Clear main spouse image errors if validation passes
      setSpouseImageValidationError(false);
      setSpouseValidationErrors((prev) => ({
        ...prev,
        image: "",
      }));
    }

    // Validate additional spouse images
    // Additional spouse images are not required for root users (same as main spouse)
    // Check if there are additional spouses (index 1 and beyond)
    if (!issingle && !isRootUser && (parentGeneration != 0 || editMemberData?.generation != 0) && grandfatherData?.maritalStatus && spouses.length > 1) {
      const additionalSpouseImageErrors = {};
      let hasAdditionalSpouseImageError = false;

      for (let i = 1; i < spouses.length; i++) {
        const spouse = spouses[i];
        const spouseImageType = `spouse-${spouse.id}`;
        const hasSpouseImage = uploadedImages.some(
          (img) => img.type === spouseImageType
        );

        // Additional spouse images are required for non-root users
        if (!hasSpouseImage) {
          additionalSpouseImageErrors[spouse.id] = "Spouse image is required";
          hasAdditionalSpouseImageError = true;
        }
      }

      if (hasAdditionalSpouseImageError) {
        setAdditionalSpouseImageErrors(additionalSpouseImageErrors);
        // Focus on first additional spouse with missing image
        const firstErrorSpouseId = Object.keys(additionalSpouseImageErrors)[0];
        // Match the original spouse id (may be a number) so the section actually opens
        const firstErrorSpouse = spouses.find(
          (spouse) => String(spouse.id) === String(firstErrorSpouseId)
        );
        revealSpouseSection(firstErrorSpouse ? firstErrorSpouse.id : firstErrorSpouseId);
        toast.error("Spouse image is required");
        setTimeout(() => {
          const spouseImageSection = document.querySelector(`#additional-spouse-image-section-${firstErrorSpouseId}`);
          if (spouseImageSection) {
            spouseImageSection.scrollIntoView({ behavior: 'smooth', block: 'center' });
          }
        }, 100);
        return;
      }

      // Clear additional spouse image errors if validation passes
      setAdditionalSpouseImageErrors({});
    }

    // Validate spouse divorce field (required for all spouses except In Relationship / Widowed)
    if (
      !issingle &&
      grandfatherData.maritalStatus &&
      grandmotherData.aliveStatus != 2 &&
      !skipDivorceAndWeddingRequired
    ) {
      // Validate main spouse divorce
      if (!grandmotherData.divorce || grandmotherData.divorce.trim() === "") {
        setSpouseValidationErrors({ divorce: "Divorce is required" });
        setAdditionalSpouseErrors({});
        revealSpouseSection("main-spouse");
        toast.error("Divorce is required for the spouse");
        setTouchedFields((prev) => ({
          ...prev,
          spouse: {
            ...prev.spouse,
            divorce: true,
          },
        }));
        setTimeout(() => {
          const divorceField = document.querySelector('#spouse-divorce-field');
          if (divorceField) {
            divorceField.scrollIntoView({ behavior: 'smooth', block: 'center' });
            const input = divorceField.querySelector('input, select, div[role="combobox"]');
            input?.focus();
          }
        }, 100);
        return;
      }

      // Validate additional spouses divorce - check each one
      for (let i = 1; i < spouses.length; i++) {
        const spouse = spouses[i];
        // if ((!spouse.divorce || spouse.divorce.trim() === "") && spouse.aliveStatus !=2) {
        if ((!spouse.divorce || String(spouse.divorce).trim() === "") && spouse.aliveStatus != 2) {
          // setSpouseValidationErrors({ divorce: "Divorce is required for all spouses" });
          setAdditionalSpouseErrors({
            [spouse.id]: { divorce: "Divorce is required" }
          });
          revealSpouseSection(spouse.id);
          toast.error("Divorce is required for the spouse");
          setTouchedFields((prev) => ({
            ...prev,
            spouse: {
              ...prev.spouse,
              divorce: true,
            },
          }));
          setTimeout(() => {
            const divorceField = document.querySelector(`#additional-spouse-divorce-field-${spouse.id}`);
            if (divorceField) {
              divorceField.scrollIntoView({ behavior: 'smooth', block: 'center' });
              const input = divorceField.querySelector('input, select, div[role="combobox"]');
              input?.focus();
            }
          }, 100);
          return;
        }
      }

      // Validate main spouse email, aliveStatus, firstName, and lastName
      const mainSpouseErrors = {
        divorce: "",
        email: "",
        aliveStatus: "",
        firstName: "",
        lastName: "",
        gender: "",
      };

      if (!grandmotherData.firstName || grandmotherData.firstName.trim() === "") {
        mainSpouseErrors.firstName = "First Name is required";
      }

      if (!grandmotherData.lastName || grandmotherData.lastName.trim() === "") {
        mainSpouseErrors.lastName = "Last Name is required";
      }

      // Email validation - optional for spouse; validate format when provided
      if (grandmotherData.email?.trim()) {
        const emailError = resolveEmailValidationError(grandmotherData.email);
        if (emailError) mainSpouseErrors.email = emailError;
      }

      if (!grandmotherData.aliveStatus || grandmotherData.aliveStatus.trim() === "") {
        mainSpouseErrors.aliveStatus = "Living / Deceased status is required";
      }

      if (!grandmotherData.gender) {
        mainSpouseErrors.gender = "Gender is required";
      }

      // Validate additional spouses separately
      let additionalSpouseWithError = null;
      const additionalSpouseValidationErrors = {};
      for (let i = 1; i < spouses.length; i++) {
        const spouse = spouses[i];
        const spouseErrors = {};

        if (!spouse.firstName || spouse.firstName.trim() === "") {
          spouseErrors.firstName = "First Name is required";
          if (!additionalSpouseWithError) additionalSpouseWithError = spouse;
        }
        if (!spouse.lastName || spouse.lastName.trim() === "") {
          spouseErrors.lastName = "Last Name is required";
          if (!additionalSpouseWithError) additionalSpouseWithError = spouse;
        }
        // Email validation - optional for spouse; validate format when provided
        if (spouse.email?.trim()) {
          const emailError = resolveEmailValidationError(spouse.email);
          if (emailError) {
            spouseErrors.email = emailError;
            if (!additionalSpouseWithError) additionalSpouseWithError = spouse;
          }
        }
        if (!spouse.aliveStatus || spouse.aliveStatus.trim() === "") {
          spouseErrors.aliveStatus = "Living / Deceased status is required";
          if (!additionalSpouseWithError) additionalSpouseWithError = spouse;
        }
        if (!spouse.gender) {
          spouseErrors.gender = "Gender is required";
          if (!additionalSpouseWithError) additionalSpouseWithError = spouse;
        }

        // Store errors for this specific additional spouse
        if (Object.keys(spouseErrors).length > 0) {
          additionalSpouseValidationErrors[spouse.id] = spouseErrors;
        }

      }

      // Check if there are any errors
      const hasMainSpouseErrors = mainSpouseErrors.firstName || mainSpouseErrors.lastName || mainSpouseErrors.email || mainSpouseErrors.aliveStatus || mainSpouseErrors.gender;
      const hasAdditionalSpouseErrors = Object.keys(additionalSpouseValidationErrors).length > 0;

      if (hasMainSpouseErrors || hasAdditionalSpouseErrors) {
        // Set main spouse errors only
        setSpouseValidationErrors(mainSpouseErrors);
        // Set additional spouse errors separately
        setAdditionalSpouseErrors(additionalSpouseValidationErrors);

        // Spouse sections may be collapsed - open the one holding the error
        if (hasAdditionalSpouseErrors && additionalSpouseWithError) {
          revealSpouseSection(additionalSpouseWithError.id);
        } else {
          revealSpouseSection("main-spouse");
        }
        toast.error(
          Object.values(mainSpouseErrors).find((message) => message) ||
          Object.values(
            additionalSpouseValidationErrors[additionalSpouseWithError?.id] || {}
          ).find((message) => message) ||
          "Please fix the highlighted spouse fields"
        );

        setTouchedFields((prev) => ({
          ...prev,
          spouse: {
            ...prev.spouse,
            firstName: true,
            lastName: true,
            email: true,
            aliveStatus: true,
            stateOfBirth: true,
            gender: true,
          },
        }));

        // Focus on the field with error
        setTimeout(() => {
          if (hasAdditionalSpouseErrors && additionalSpouseWithError) {
            // Error is in additional spouse
            const errors = additionalSpouseValidationErrors[additionalSpouseWithError.id];
            if (errors.firstName) {
              const firstNameField = document.querySelector(`#additional-spouse-${additionalSpouseWithError.id}-firstName`);
              if (firstNameField) {
                firstNameField.scrollIntoView({ behavior: 'smooth', block: 'center' });
                firstNameField.focus();
              }
            } else if (errors.lastName) {
              const lastNameField = document.querySelector(`#additional-spouse-${additionalSpouseWithError.id}-lastName`);
              if (lastNameField) {
                lastNameField.scrollIntoView({ behavior: 'smooth', block: 'center' });
                lastNameField.focus();
              }
              // } 
            } else if (errors.email) {
              const emailField = document.querySelector(`#additional-spouse-email-field-${additionalSpouseWithError.id}`);
              if (emailField) {
                emailField.scrollIntoView({ behavior: 'smooth', block: 'center' });
                emailField.querySelector('input')?.focus();
              }
            } else if (errors.aliveStatus) {
              const aliveStatusField = document.querySelector(`#additional-spouse-alive-status-field-${additionalSpouseWithError.id}`);
              if (aliveStatusField) {
                aliveStatusField.scrollIntoView({ behavior: 'smooth', block: 'center' });
                const input = aliveStatusField.querySelector('input, select, div[role="combobox"]');
                input?.focus();
              }
            } else if (errors.gender) {
              const genderField = document.querySelector(`#gender-field-${additionalSpouseWithError.id}`);
              if (genderField) {
                genderField.scrollIntoView({ behavior: 'smooth', block: 'center' });
                const input = genderField.querySelector('input, select, div[role="combobox"]');
                input?.focus();
              }
            }
          } else if (hasMainSpouseErrors) {
            // Error is in main spouse
            if (mainSpouseErrors.firstName) {
              const firstNameField = document.querySelector('#grandmother-firstName');
              if (firstNameField) {
                firstNameField.scrollIntoView({ behavior: 'smooth', block: 'center' });
                firstNameField.focus();
              }
            }
            else if (mainSpouseErrors.lastName) {
              const lastNameField = document.querySelector('#grandmother-lastName');
              if (lastNameField) {
                lastNameField.scrollIntoView({ behavior: 'smooth', block: 'center' });
                lastNameField.focus();
              }
            }
            else if (mainSpouseErrors.gender) {
              const genderField = document.querySelector('#gender-field-spouse');
              if (genderField) {
                genderField.scrollIntoView({ behavior: 'smooth', block: 'center' });
                const input = genderField.querySelector('input, select, div[role="combobox"]');
                input?.focus();
              }
            } else if (mainSpouseErrors.email) {
              const emailField = document.querySelector('#spouse-email-field');
              if (emailField) {
                emailField.scrollIntoView({ behavior: 'smooth', block: 'center' });
                emailField.querySelector('input')?.focus();
              }
            } else if (mainSpouseErrors.aliveStatus) {
              const aliveStatusField = document.querySelector('#spouse-alive-status-field');
              if (aliveStatusField) {
                aliveStatusField.scrollIntoView({ behavior: 'smooth', block: 'center' });
                const input = aliveStatusField.querySelector('input, select, div[role="combobox"]');
                input?.focus();
              }
            }
          }
        }
          , 100);
        return;
      }

      // Clear errors if validation passes
      setSpouseValidationErrors({ divorce: "", email: "", gender: "", aliveStatus: "", firstName: "", lastName: "" });
      setAdditionalSpouseErrors({});
    }

    // Validate dates
    if (!validateDates()) {
      // Check if there are chronological validation errors
      const hasChronologicalErrors = !areAllDateValidationsPassing(
        dateValidationErrors
      );

      if (hasChronologicalErrors) {
        toast.error(
          "Please fix date validation errors. Dates must be in chronological order."
        );
      } else {
        toast.error(
          "Please complete all required date fields (month, day, and year)"
        );
      }
      return;
    }
    // ... (Existing handleSubmit logic)

    if (!isEditMode) {
      // Phone validation
      if (!grandfatherData.phone || grandfatherData.phone.trim() === "") {
        // toast.error("Mobile number is required");
        // setPhoneValidationError(true);
        // return;
      }
    }

    // Safely extract parent member ID
    const relatedMemberId =
      parentMember && parentMember.id ? parentMember.id : null;
    //console.log('Related Member ID:', relatedMemberId);

    // Extract family_tree_id from parentMember or familyTreeId prop
    const extractedFamilyTreeId =
      familyTreeId ||
      parentMember?.family_tree_id ||
      parentMember?.familyTreeId ||
      null;
    //console.log('Family Tree ID:', extractedFamilyTreeId);

    const isSingle = grandfatherData.maritalStatus === "1";

    // Prepare the payload matching the API structure
    const payload = {
      // Root head family data from RootHeadModal
      root_head_family_id: rootHeadData.selectedHeadId || null,
      tree_color_name: rootHeadData.colorName || "",
      tree_hex_code: rootHeadData.selectedColor || "",
      ...(rootHeadData?.tree_side
        ? { tree_side: rootHeadData.tree_side }
        : {}),

      // Family tree ID
      family_tree_id: extractedFamilyTreeId,

      // Parent relationship (if adding child to existing member)
      related_member_id: relatedMemberId,

      // Main member (Grandfather) data - trim all text values
      first_name: grandfatherData.firstName?.trim() || "",
      middle_name: grandfatherData.middleName?.trim() || "",
      special_name: grandfatherData.specialName?.trim() || "",
      last_name: grandfatherData.lastName?.trim() || "",
      suffix: grandfatherData.suffix?.trim() || "",
      maiden_name: grandfatherData.maidenName?.trim() || "",
      nick_name: grandfatherData.nickName?.trim() || "",
      gender: grandfatherData.gender ? parseInt(grandfatherData.gender) : null, // Convert to integer
      dob: convertDateToTimestamp(grandfatherData.dob), // Convert to timestamp at 00:00:00
      state_of_birth: grandfatherData.stateOfBirth,
      current_state_residing: grandfatherData.currentStateResiding,
      present_or_deceased: grandfatherData.aliveStatus
        ? parseInt(grandfatherData.aliveStatus)
        : null, // Convert to integer
      date_of_death:
        grandfatherData.aliveStatus === "2"
          ? convertDateToTimestamp(grandfatherData.dod)
          : null, // Convert to timestamp at 00:00:00, only if deceased
      // 5. SEND ageOfDeath AS A NUMBER, NOT A TIMESTAMP
      age_at_death: grandfatherData.ageOfDeath
        ? parseInt(grandfatherData.ageOfDeath)
        : null,
      marital_status: grandfatherData.maritalStatus
        ? parseInt(grandfatherData.maritalStatus)
        : null, // Convert to integer
      wedding_anniversary: isInRelationshipMaritalStatus(grandfatherData.maritalStatus)
        ? null
        : convertDateToTimestamp(grandfatherData.weddingAnniversary), // Convert to timestamp at 00:00:00
      notes: grandfatherData.note?.trim() || "", // API uses 'notes' (plural) - trim value
      relation: grandfatherData.relation?.trim() || "",
      // Send empty email if Deceased (aliveStatus === "2"), otherwise send the email value
      email: grandfatherData.aliveStatus === "2" ? "" : (grandfatherData.email?.trim() || ""),
      phone: grandfatherData.phone ? extractPhoneNumber(grandfatherData.phone, grandfatherData.countryCode) : null,
      country_code: grandfatherData.countryCode,
      relation_to_head_of_line: grandfatherData.relation_to_head_of_line?.trim() || "",
      bio_descendant_parent: grandfatherData.bio_descendant_parent?.trim() || "",
      bio_non_descendant_parent: grandfatherData.bio_non_descendant_parent?.trim() || "",
      step_parent: grandfatherData.step_parent?.trim() || "",
      adopted_parent: grandfatherData.adopted_parent?.trim() || "",
      phone_type: grandfatherData.phone_type
        ? parseInt(grandfatherData.phone_type)
        : null,
      // color_name: rootHeadData.colorName || "",
      // hex_code: rootHeadData.selectedColor || "",
      // color_code: rootHeadData.selectedColor || "",  // Added color_code
      color_name: isEditMode
        ? selectedProfileColor?.colorName ||
        rootHeadData.colorName ||
        editMemberData?.colorName ||
        ""
        : rootHeadData.colorName || "",
      hex_code: isEditMode
        ? selectedProfileColor?.selectedColor ||
        rootHeadData.selectedColor ||
        editMemberData?.colorCode ||
        ""
        : rootHeadData.selectedColor || "",
      color_code: isEditMode
        ? selectedProfileColor?.selectedColor ||
        rootHeadData.selectedColor ||
        editMemberData?.colorCode ||
        ""
        : rootHeadData.selectedColor || "", // Added color_code
      phone_type: grandfatherData.phone_type
        ? parseInt(grandfatherData.phone_type)
        : null,
      relation_to_head_of_line: grandfatherData.relation_to_head_of_line || "",
      bio_descendant_parent: grandfatherData.bio_descendant_parent || "",
      bio_non_descendant_parent:
        grandfatherData.bio_non_descendant_parent || "",
      step_parent: grandfatherData.step_parent || "",
      adopted_parent: grandfatherData.adopted_parent || "",
      image: editMemberData?.photo || "",
      multiple_births_type: grandfatherData.multiple_births_type ? grandfatherData.multiple_births_type : null,
      // Multiple birth related fields
      multiple_birth_member_user_id: (() => {
        // Legacy support: If multiple_birth_with_member is selected, find the sibling and get their user_id
        if (grandfatherData.multiple_birth_with_member && parentChildren && parentChildren.length > 0) {
          const selectedSibling = parentChildren.find(
            child => child.id?.toString() === grandfatherData.multiple_birth_with_member?.toString()
          );
          if (selectedSibling) {
            // Check all possible locations for user_id
            const userId = selectedSibling.user_id || 
                          selectedSibling.userId || 
                          selectedSibling.User?.id || 
                          selectedSibling.User?.user_id || "";
            
            console.log("🔵 [handleFormSubmit] Multiple birth - Selected sibling user_id extraction:", {
              siblingId: selectedSibling.id,
              siblingName: selectedSibling.first_name || selectedSibling.name?.split(' ')[0] || "sibling",
              user_id_found: userId,
              user_id_sources: {
                'selectedSibling.user_id': selectedSibling.user_id,
                'selectedSibling.userId': selectedSibling.userId,
                'selectedSibling.User?.id': selectedSibling.User?.id,
                'selectedSibling.User?.user_id': selectedSibling.User?.user_id
              },
              allSiblingKeys: Object.keys(selectedSibling),
              siblingUserObject: selectedSibling.User,
              completeSiblingData: selectedSibling
            });
            
            if (!userId) {
              console.warn("⚠️ [handleFormSubmit] user_id not found for selected sibling:", {
                siblingId: selectedSibling.id,
                siblingName: selectedSibling.first_name || selectedSibling.name,
                availableKeys: Object.keys(selectedSibling)
              });
            }
            
            return userId;
          } else {
            console.warn("⚠️ [handleFormSubmit] Selected sibling not found in parentChildren:", {
              selectedId: grandfatherData.multiple_birth_with_member,
              parentChildrenCount: parentChildren.length,
              parentChildrenIds: parentChildren.map(c => c.id)
            });
          }
        }
        return "";
      })(),
      multiple_birth_order_number: (() => {
        const order = grandfatherData.multiple_birth_order;
        if (!order) return "";
        const orderNum = typeof order === 'string' ? parseInt(order, 10) : order;
        return isNaN(orderNum) ? "" : orderNum.toString();
      })(),
      // New multiple_birth_details format - array of objects with order and family_member_id; null when no multiple birth
      // In add mode: includes current member (family_member_id: null) + selected siblings
      // In edit mode: includes current member (with their ID) + selected siblings
      multiple_birth_details: (() => {
        if (!grandfatherData.multiple_births_type) return null;
        const details = grandfatherData.multiple_birth_details || [];
        const currentMemberOrder = grandfatherData.multiple_birth_order;
        const result = [];
        
        // Include current member if they have an order
        if (currentMemberOrder) {
          const currentOrderNum = typeof currentMemberOrder === 'string' ? parseInt(currentMemberOrder, 10) : currentMemberOrder;
          if (!isNaN(currentOrderNum) && currentOrderNum > 0) {
            if (isEditMode && editMemberData?.id) {
              // In edit mode: include current member with their actual ID
              result.push({
                order: currentOrderNum,
                family_member_id: editMemberData.id.toString()
              });
            } else if (!isEditMode) {
              // In add mode: include current member with family_member_id: null
              result.push({
                order: currentOrderNum,
                family_member_id: null
              });
            }
          }
        }
        
        // Add all selected siblings with valid orders
        if (Array.isArray(details) && details.length > 0) {
          console.log("🔵 [handleFormSubmit] Multiple birth details:", details);
          const currentMemberId = isEditMode && editMemberData?.id ? editMemberData.id.toString() : null;
          
          const validSiblingDetails = details
            .filter(detail => {
              // Skip if this is the current member's entry (we already added it above)
              const detailId = detail.family_member_id?.toString() || "";
              if (currentMemberId && detailId === currentMemberId) {
                return false;
              }
              
              // Only include entries with valid orders
              const order = detail.order;
              const orderNum = typeof order === 'string' ? parseInt(order, 10) : order;
              return order && order !== "" && !isNaN(orderNum) && orderNum > 0;
            })
            .map(detail => {
              const order = detail.order;
              const orderNum = typeof order === 'string' ? parseInt(order, 10) : order;
              return {
                order: orderNum,
                family_member_id: detail.family_member_id?.toString() || ""
              };
            });
          
          result.push(...validSiblingDetails);
        }
        
        // Return array if there are valid details, otherwise null (so API clears multiple birth)
        return result.length > 0 ? result : null;
      })(),
    };

    // Conditionally include Spouse data only if NOT Single
    if (!isSingle || (isEditMode && spouses.length > 0)) {
      const spousesArray = [];

      // Process all spouses from the consolidated array
      spouses.forEach((spouse, index) => {
        // Only include spouse if they have at least a first or last name
        if (spouse.firstName || spouse.lastName) {
          const spouseImageType = `spouse-${spouse.id}`;
          const spouseImage = uploadedImages.find(
            (img) => img.type === spouseImageType
          );

          spousesArray.push({
            relation_history_id: spouse?.relation_history_id || "",
            spouse_first_name: spouse.firstName?.trim() || "",
            spouse_middle_name: spouse.middleName?.trim() || "",
            spouse_special_name: spouse.specialName?.trim() || "",
            spouse_last_name: spouse.lastName?.trim() || "",
            spouse_suffix: spouse.suffix?.trim() || "",
            spouse_maiden_name: spouse.maidenName?.trim() || "",
            spouse_nick_name: "",
            spouse_gender: spouse.gender ? parseInt(spouse.gender) : null,
            spouse_dob: convertDateToTimestamp(spouse.dob),
            spouse_state_of_birth: spouse.stateOfBirth,
            spouse_current_state_residing: spouse.currentStateResiding,
            spouse_date_of_death:
              spouse.aliveStatus == "2" ? convertDateToTimestamp(spouse.dod) : null,
            spouse_age_at_death: spouse.ageOfDeath
              ? parseInt(spouse.ageOfDeath)
              : null,
            spouse_relation: spouse.relation?.trim() || grandmotherData.relation?.trim() || "",
            spouse_present_or_deceased: spouse.aliveStatus
              ? parseInt(spouse.aliveStatus)
              : null,
            // Send empty email if Deceased (aliveStatus === "2"), otherwise send the email value
            spouse_email: spouse.aliveStatus === "2" ? "" : (spouse.email?.trim() || ""),
            spouse_phone: spouse.phone ? extractPhoneNumber(spouse.phone, spouse.countryCode) : null,
            spouse_country_code: spouse.countryCode,
            spouse_phone_type: spouse.phone_type
              ? parseInt(spouse.phone_type)
              : null,
            // Additional fields
            divorce: isInRelationshipMaritalStatus(grandfatherData.maritalStatus)
              ? null
              : spouse.divorce
                ? parseInt(spouse.divorce)
                : null,
            divorce_date: isInRelationshipMaritalStatus(grandfatherData.maritalStatus)
              ? null
              : spouse.divorce_date
                ? convertDateToTimestamp(spouse.divorce_date)
                : null,
            // wedding_anniversary: convertDateToTimestamp(spouse.weddingAnniversary),
            address: spouse.address?.trim() || "",
            notes: spouse.note?.trim() || "",
            // multiple_births_type: spouse.multiple_births_type || "",
            relation_to_head_of_line: spouse.relation_to_head_of_line?.trim() || "",
            // spouse_image: spouseImage ? (spouseImage.file || spouseImage.url) : "",
            spouse_wedding_anniversary: isInRelationshipMaritalStatus(
              grandfatherData.maritalStatus
            )
              ? null
              : convertDateToTimestamp(
                  spouse.weddingAnniversary || spouse.wedding_anniversary || ""
                ),
          });
        }
      });

      // Add spouses array to payload
      if (spousesArray.length > 0) {
        payload.spouses = spousesArray;
      }


    }

    // console.log("Uploaded images:", uploadedImages);
    if (uploadedImages.length > 0) {
      // Find new images that have file objects (not existing images from edit mode)
      const newImages = uploadedImages.filter((img) => img.file);

      // Assign images by type instead of index
      const mainImage = newImages.find((img) => img.type == "main");
      const spouseImage = newImages.find((img) => img.type == "spouse");
      // const mainImage = uploadedImages.find(img => img.type == 'main');
      // const spouseImage = uploadedImages.find(img => img.type == 'spouse');

      // Find existing images that are being kept (have unique_image_id but no file)
      const keptExistingImages = uploadedImages.filter(
        (img) => img.unique_image_id && !img.file
      );


      // Add main image to payload (always included if present)
      if (mainImage) {
        if (mainImage.file) {
          payload.image = mainImage.file;
          console.log(
            "Added main member image for",
            payload.first_name || "Main Member:",
            mainImage.file.name
          );


        }

      }

      // Handle spouse images for all spouses
      spouses.forEach((spouse, spouseIndex) => {
        // For first spouse (index 0), use 'spouse-main-spouse', otherwise use 'spouse-${spouse.id}'
        const spouseType = spouseIndex === 0 ? 'spouse-main-spouse' : `spouse-${spouse.id}`;
        const spouseImages = uploadedImages.filter(img => img.type === spouseType); spouseImages.forEach((img, imageIndex) => {
          if (img.file) {
            // New or replaced image
            const fieldName = `spouse_image_${spouseIndex}_${imageIndex}`;
            if (!payload.spouse_images) payload.spouse_images = {};
            payload.spouse_images[fieldName] = img.file;
          }
        });
      });  // If you need to track deleted images, you can add them too
      // This would require comparing with the original images from editMemberData
      if (isEditMode && editMemberData.userData) {
        // Collect all original image IDs from member_images and spouse_images
        const originalImageIds = [];    // Add primary member image IDs
        if (editMemberData.userData.member_images && Array.isArray(editMemberData.userData.member_images)) {
          editMemberData.userData.member_images.forEach(img => {
            if (img.role === 0 && img.id) {
              originalImageIds.push(img.id);
            }
          });
        }    // Add spouse image IDs from all_spouses
        if (editMemberData.userData.all_spouses && Array.isArray(editMemberData.userData.all_spouses)) {
          editMemberData.userData.all_spouses.forEach(spouse => {
            if (spouse.spouse_images && Array.isArray(spouse.spouse_images)) {
              spouse.spouse_images.forEach(img => {
                if (img.role === 1 && img.id) {
                  originalImageIds.push(img.id);
                }
              });
            }
          });
        }    // Get images that are being replaced (have unique_image_id and file)
        // These are previous images that are being replaced with new ones
        // When user uploads a new image to replace existing one, the old image ID is preserved in unique_image_id
        const replacedImageIds = uploadedImages
          .filter(img => img.unique_image_id && img.file)
          .map(img => img.unique_image_id); console.log(":arrows_anticlockwise: Replaced image IDs (old images being replaced with new ones):", replacedImageIds);    // Get images that are being kept (have unique_image_id but no file)
        // These are existing images that are displayed but not replaced
        const keptImageIds = uploadedImages
          .filter(img => img.unique_image_id && !img.file)
          .map(img => img.unique_image_id); console.log(":white_tick: Kept image IDs (existing images not changed):", keptImageIds);    // Images to remove:
        // 1. Images that are replaced (old image ID when new image is uploaded) - same as clicking cross
        // 2. Images that are deleted (in original but not in current uploadedImages)
        const allCurrentImageIds = [...replacedImageIds, ...keptImageIds];
        const deletedImageIds = originalImageIds.filter(id => !allCurrentImageIds.includes(id)); console.log(":x: Deleted image IDs (removed from UI):", deletedImageIds);    // Combine replaced and deleted image IDs
        // Both replaced and deleted images should be removed from backend
        const removeImageIds = [...replacedImageIds, ...deletedImageIds];    // Always send remove_image_ids, even if empty array
        payload.remove_image_ids = JSON.stringify(removeImageIds);
        console.log(":rubbish_bin: Final remove_image_ids array (replaced + deleted):", removeImageIds);
      }

    }

    console.log("📤 Sending payload to API:", payload);

    try {
      // ... (Existing API call logic)
      setLoading(true);

      // Create FormData and append all payload data plus images
      const formData = new FormData();

      // Append all payload fields to FormData
      Object.keys(payload).forEach((key) => {
        const value = payload[key];
        // Explicitly send null for multiple_birth fields so backend can clear them
        if (key === "multiple_births_type" || key === "multiple_birth_details") {
          if (value === null || value === undefined || value === "") {
            formData.append(key, "null");
            return;
          }
        }
        if (value !== null && value !== undefined) {
          if (key === "spouses" && Array.isArray(value)) {
            /**
             * IMPORTANT:
             * Swagger / curl sends `spouses` as ONE JSON string field, e.g.:
             *   spouses='[{"spouse_first_name":"Mary",...}]'
             * but our previous code was sending fields like:
             *   spouses[0][spouse_first_name]=Mary
             *
             * The backend only knows how to handle the JSON-string version,
             * so we must mirror that behaviour here.
             */
            formData.append("spouses", JSON.stringify(value));
          } else if (key === "multiple_birth_details" && (Array.isArray(value) || typeof value === "object")) {
            // Stringify multiple_birth_details array/object to JSON string
            formData.append("multiple_birth_details", JSON.stringify(value));
          } else {
            formData.append(key, value);
          }
        }
      });

      // Append all payload fields to FormData
      // Handle spouse images with indexed field names:
      // spouse 0 -> spouse_image_0_0, spouse_image_0_1, ...
      // spouse 1 -> spouse_image_1_0, spouse_image_1_1, ...
      if (payload.spouses && Array.isArray(payload.spouses)) {
        payload.spouses.forEach((spouse, spouseIndex) => {
          // Get the corresponding spouse from the spouses array to find the correct image type
          const spouseData = spouses[spouseIndex];
          if (spouseData) {
            const spouseType = spouseIndex === 0 ? 'spouse-main-spouse' : `spouse-${spouseData.id}`;

            // Get all new images (File objects) for this spouse
            const spouseImages = uploadedImages.filter(
              (img) => img.type === spouseType && img.file
            );

            spouseImages.forEach((img, imageIndex) => {
              const fieldName = `spouse_image_${spouseIndex}_${imageIndex}`;
              formData.append(fieldName, img.file);
            });
          }
        });
      }

      console.log("📤 Sending FormData to API:");
      for (let [key, value] of formData.entries()) {
        if (value instanceof File) {
          console.log(`${key}: File(${value.name}, ${value.size} bytes)`);
        } else {
          console.log(`${key}: ${value}`);
        }
      }

      let response;
      if (isEditMode && editMemberData) {
        // Use edit API endpoint
        console.log(
          "API Endpoint: PUT /family-members-with-relations/" +
          editMemberData.id
        );
        response = await putApi(
          `/family-members-with-relations-v2/${editMemberData.id}`,
          formData,
          {
            headers: {
              "Content-Type": "multipart/form-data",
            },
          }
        );
      } else {
        // Use create API endpoint
        console.log("API Endpoint: /family-members-with-relations");
        response = await postApi(
          "/family-members-with-relations-v2",
          formData,
          {
            headers: {
              "Content-Type": "multipart/form-data",
            },
          }
        );
      }

      console.log("📥 API Response:", response);

      if (response?.statusCode === 200) {
        console.log(
          isEditMode
            ? "✅ Member updated successfully"
            : "✅ Member created successfully"
        );
        setLoading(false);
        toast.success(
          isEditMode
            ? "Family member updated successfully!"
            : "Family member added successfully!"
        );

        // If response contains family_tree_id, update localStorage
        // Always update if this is a new root member (not edit mode and isRootUser)
        // Or if user doesn't have one yet
        const familyTreeId = response?.data?.family_tree_id ||
          response?.data?.familyTreeId ||
          response?.family_tree_id ||
          response?.familyTreeId;

        if (familyTreeId) {
          try {
            const shouldUpdate =
              (!isEditMode && isRootUser) || !getFamilyTreeIdFromStorage();
            if (shouldUpdate) {
              setFamilyTreeIdInLoggedInRole(familyTreeId);
              console.log(
                "✅ Saved family tree id on loggedIn_user_role:",
                familyTreeId
              );
            }
          } catch (error) {
            console.error("Error persisting family tree id:", error);
          }
        }

        if (onNext) {
          onNext(response.data);
        }
        onClose();
      } else {
        console.error("❌ API returned error:", response);
        setLoading(false);
        toast.error(
          response?.message ||
          (isEditMode
            ? "Failed to update family member"
            : "Failed to create family member")
        );
      }
    } catch (error) {
      setLoading(false);
      console.error(
        isEditMode
          ? "❌ Error updating family member:"
          : "❌ Error creating family member:",
        error
      );
      console.error("Error details:", error.response || error.message);
      toast.error(
        isEditMode
          ? "Failed to update family member. Please try again."
          : "Failed to create family member. Please try again."
      );
    } finally {
      setLoading(false);
    }
  };